import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import { exportElementToPdf } from './exportElementToPdf';

interface UsePdfExportOptions {
    fileName: string | (() => string);
    onError?: (err: unknown) => void;
}

interface UsePdfExportReturn {
    isExporting: boolean;
    exportToPdf: () => void;
    exportHostRef: RefObject<HTMLDivElement | null>;
}

/**
 * State machine for capturing an off-screen element as a PDF.
 *
 * Call `exportToPdf()` to begin. The consumer is expected to render a
 * `<PdfExportHost ref={exportHostRef}>` containing the content to capture,
 * conditional on `isExporting` so it only mounts during export.
 */
export function usePdfExport(options: UsePdfExportOptions): UsePdfExportReturn {
    const exportHostRef = useRef<HTMLDivElement | null>(null);
    const [exporting, setExporting] = useState(false);

    const { fileName, onError } = options;

    const exportToPdf = useCallback(() => {
        if (exporting) return;
        setExporting(true);
    }, [exporting]);

    useEffect(() => {
        if (!exporting) return;

        let cancelled = false;
        // Wait one frame so the off-screen host has rendered and its layout
        // (sticky cells, MUI styles) is settled before capture.
        const raf = requestAnimationFrame(async () => {
            if (cancelled) return;
            const el = exportHostRef.current;
            if (!el) {
                setExporting(false);
                return;
            }
            try {
                const name = typeof fileName === 'function' ? fileName() : fileName;
                await exportElementToPdf(el, { fileName: name });
            } catch (err) {
                console.error('Failed to export PDF', err);
                onError?.(err);
            } finally {
                if (!cancelled) setExporting(false);
            }
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
        };
    }, [exporting, fileName, onError]);

    return { isExporting: exporting, exportToPdf, exportHostRef };
}
